import type { Option } from '@design-system/components/Inputs/InputsSelect.props'
import { countryPrefixes } from '@design-system/data/countryPrefixes'

/**
 * Represents the structure of a book an appointment form.
 * @typedef {Object} BookAnAppointmentForm
 * @property {string} country - Selected country
 * @property {string} city - Selected city
 * @property {string} shop - Selected shop
 * @property {string} firstName - User's first name
 * @property {string} lastName - User's last name
 * @property {string} email - User's email address
 * @property {string} phonePrefix - Phone number country prefix
 * @property {string} phoneNumber - User's phone number
 * @property {string} message - Optional message
 */
export type BookAnAppointmentForm = {
  country: string
  city: string
  shop: string
  firstName: string
  lastName: string
  email: string
  phonePrefix: string
  phoneNumber: string
  message: string
}

/**
 * Composable for managing book an appointment functionality.
 * Handles form initialization, data fetching, and submission for booking appointments.
 *
 * @param {string} [brand] - Optional brand name to customize appointment booking
 * @returns {Promise<Object>} An object containing appointment booking-related data and methods
 *
 * @example
 * const {
 *   bookAnAppointmentForm,
 *   countriesOptions,
 *   handleSubmitBookAnAppointment
 * } = await useBookAnAppointment('giorgio-armani');
 */
export const useBookAnAppointment = async (brand?: string) => {
  const { country } = useRouteHelper()
  const route = useRoute()
  const { currentWebsite } = useWebsite()
  const { language } = useRouteHelper()
  const { customer } = await useCustomer()
  const { fetchBookAppontment } = await useBookAndReserve()

  /**
   * Reactive form for booking an appointment.
   * Pre-populated with customer data if available.
   *
   * @type {Ref<BookAnAppointmentForm>}
   */
  const bookAnAppointmentForm = ref<BookAnAppointmentForm>({
    country: '',
    city: '',
    shop: '',
    firstName: customer?.value?.firstName ?? '',
    lastName: customer?.value?.lastName ?? '',
    email: customer?.value?.email ?? '',
    phonePrefix: customer?.value?.prefix ?? '',
    phoneNumber: customer?.value?.phoneNumber ?? '',
    message: '',
  })

  /**
   * Reactive list of city options for appointment booking.
   * @type {Ref<Option[]>}
   */
  const citiesOptions = ref<Option[]>([])

  /**
   * Reactive list of shop options for appointment booking.
   * @type {Ref<Option[]>}
   */
  const shopsOptions = ref<Option[]>([])

  /**
   * Tracks whether query parameters have been loaded.
   * @type {Ref<boolean>}
   */
  const queryParamLoaded = ref<boolean>(false)

  /**
   * Computes the brand-specific country variations.
   * @returns {string[]} Array of country variations for the current brand
   */
  const brandCountry = computed(() => {
    switch (brand ?? currentWebsite.value) {
      case 'giorgio-armani':
        return ['giorgio-armani', 'giorgioarmani']
      case 'emporio-armani':
        return ['emporio-armani', 'emporioarmani']
      case 'ea7':
        return ['ea7']
      case 'armani-exchange':
        return ['ax-armani-exchange', 'ax-armaniexchange', 'axarmaniexchange']
      case 'armani-casa':
        return ['armani-casa', 'armani/casa']

      default:
        return ''
    }
  })

  /**
   * Computes the brand-specific shop identifier.
   * @returns {string} Shop identifier for the current brand
   */
  const brandShop = computed(() => {
    switch (brand ?? currentWebsite.value) {
      case 'giorgio-armani':
        return 'GIORGIO_ARMANI'
      case 'emporio-armani':
        return 'EMPORIO_ARMANI'
      case 'ea7':
        return 'EA7'
      case 'armani-exchange':
        return 'ARMANI_EXCHANGE'
      case 'armani-casa':
        return 'ARMANI_CASA'

      default:
        return ''
    }
  })

  /**
   * Generates a version string based on the current date.
   * Used for API versioning and caching.
   *
   * @returns {string} Version string in YYYYMMDD format
   */
  const version = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  }

  // Fetch countries for appointment booking
  const { data: countriesOptions } = await useFetch(
    '/api/yext/getBookAnAppointmentCountries',
    {
      query: {
        v: version(),
        name: brandCountry,
        locale: language,
      },
      transform(data) {
        if (data) {
          return data
            .map(country => ({
              label: country.c_addressCountryDisplayName,
              value: country.name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        } else {
          return []
        }
      },
    }
  )

  /**
   * Computed property for the currently selected country in the appointment form.
   * @type {ComputedRef<string>}
   */
  const bookAnAppointmentSelectedCountry = computed(
    () => bookAnAppointmentForm.value.country
  )

  /**
   * Computes query parameters for fetching shops based on selected country.
   * @type {ComputedRef<Object>}
   */
  const bookAnAppointmentShopsQuery = computed(() => ({
    v: version(),
    c_folderBrand: brandShop.value,
    countryCode: bookAnAppointmentSelectedCountry.value,
    locale: language,
  }))

  // Fetch shops for the selected country
  const { data: shopsFromCurrentState } = await useFetch(
    '/api/yext/getBookAnAppointmentShops',
    {
      query: bookAnAppointmentShopsQuery,
      immediate: false,
      transform(data) {
        if (data) {
          const mapWithoutDuplicates = new Map<string, Option>()
          data.forEach(shop => {
            if (shop.address.countryCode !== 'JP')
              mapWithoutDuplicates.set(shop.address.city, {
                label: shop.address.city,
                value: shop.address.city,
              })
            else if (shop.address.region)
              mapWithoutDuplicates.set(shop.address.region, {
                label: shop.address.region,
                value: shop.address.city,
              })
          })
          citiesOptions.value = [...mapWithoutDuplicates.values()].sort(
            (a, b) => a.label.localeCompare(b.label)
          )

          return data
        } else {
          citiesOptions.value = []

          return []
        }
      },
    }
  )

  /** Checks whether or not the current iteration of an element of the `countryPrefix` array matches the current country in the URL */
  const isCurrentCountry = (prefixOption: { label: string }) =>
    prefixOption.label.startsWith(country.toUpperCase() + ' ')

  /**
   * List of country phone prefixes for form selection.
   * @type {Option[]}
   */
  const prefixes = countryPrefixes
    .map(countryPrefix => ({
      label: `${countryPrefix?.code} ${countryPrefix?.dial_code}`,
      value: `${countryPrefix?.dial_code}`,
    }))
    .sort((a, b) => {
      // The label for the current country should be preselected and always on top of the list
      return isCurrentCountry(a)
        ? -1
        : isCurrentCountry(b)
          ? 1
          : a.label.localeCompare(b.label)
    })

  /**
   * Handles form submission for booking an appointment.
   * Fetches and processes the appointment booking.
   *
   * @throws {Error} If booking fails
   */
  const handleSubmitBookAnAppointment = async () => {
    try {
      const store = shopsFromCurrentState.value!.find(
        shop => shop.id === bookAnAppointmentForm.value.shop
      )
      fetchBookAppontment(store!, bookAnAppointmentForm.value)
    } catch (error) {
      console.error(error)
    }
  }

  // Watchers for dynamic form behavior
  watch(bookAnAppointmentSelectedCountry, () => {
    bookAnAppointmentForm.value.city = ''
    bookAnAppointmentForm.value.shop = ''
  })

  watch(shopsFromCurrentState, newShopsFromState => {
    if (
      !!newShopsFromState?.length &&
      route.query.storeId &&
      !queryParamLoaded.value
    ) {
      const selectedStore = newShopsFromState.find(
        store => store.id === (route.query.storeId as string)
      )
      if (selectedStore) {
        bookAnAppointmentForm.value.city = selectedStore.address.city
      }
    }
  })

  watch(
    () => bookAnAppointmentForm.value.city,
    newVal => {
      bookAnAppointmentForm.value.shop = ''

      const mapWithoutDuplicates = new Map<string, Option>()
      shopsFromCurrentState
        .value!.filter(shop => shop.address.city === newVal)
        .forEach(shop => {
          mapWithoutDuplicates.set(shop.c_siteID, {
            label: shop.c_internalName,
            value: shop.id!,
          })
        })
      shopsOptions.value = [...mapWithoutDuplicates.values()].sort((a, b) =>
        a.label.localeCompare(b.label)
      )
    }
  )

  if (route.query.countryIso) {
    bookAnAppointmentForm.value.country = route.query.countryIso as string
  } else {
    const currentState = countriesOptions.value?.filter(
      countryOption => countryOption.value === country.toUpperCase()
    )[0]
    if (currentState) bookAnAppointmentForm.value.country = currentState.value
  }

  if (
    bookAnAppointmentForm.value &&
    bookAnAppointmentForm.value.phonePrefix === ''
  ) {
    const countryPrefix = prefixes.find(prefix => {
      const prefixValueLowerCase = prefix.label.toLowerCase()
      return prefixValueLowerCase.includes(country)
    })

    if (countryPrefix)
      bookAnAppointmentForm.value.phonePrefix = countryPrefix.value
  }

  watch(
    () => shopsOptions.value,
    newShopsOptions => {
      if (
        shopsFromCurrentState.value &&
        route.query.storeId &&
        !queryParamLoaded.value
      ) {
        const selectedStore = shopsFromCurrentState?.value?.find(
          store => store?.id === (route?.query?.storeId as string)
        )

        if (selectedStore && selectedStore?.id) {
          nextTick(() => {
            bookAnAppointmentForm.value.shop = selectedStore?.id
          })
        }

        queryParamLoaded.value = true
      } else if (shopsFromCurrentState.value && newShopsOptions.length === 1) {
        nextTick(() => {
          bookAnAppointmentForm.value.shop = newShopsOptions?.[0]?.value
        })
      }
    }
  )

  return {
    bookAnAppointmentForm,
    countriesOptions,
    citiesOptions,
    shopsOptions,
    country,
    route,
    queryParamLoaded,
    bookAnAppointmentSelectedCountry,
    shopsFromCurrentState,
    prefixes,
    handleSubmitBookAnAppointment,
  }
}
